import { lockScroll, unlockScroll } from './scroll-lock';

const DESKTOP = '(min-width: 1025px)';
const CLOSE_DELAY = 140;
const PANEL_FALLBACK = 450;

interface NavItem {
	root: HTMLElement;
	link: HTMLAnchorElement;
	panel: HTMLElement;
}

// The consultation popup closes the mobile menu before it opens.
let closeMenu: (() => void) | null = null;

export function closeNavMenu(): void {
	closeMenu?.();
}

export default function initNav(header: HTMLElement): void {
	const nav = header.querySelector<HTMLElement>('.armada-nav');
	const burger = header.querySelector<HTMLButtonElement>('[data-armada-burger]');
	const scrim = header.querySelector<HTMLElement>('[data-armada-scrim]');
	const desktop = window.matchMedia(DESKTOP);

	const items: NavItem[] = Array.from(header.querySelectorAll<HTMLElement>('[data-armada-nav-item]'))
		.map((root) => {
			const link = root.querySelector<HTMLAnchorElement>('.armada-nav__link');
			const panel = root.querySelector<HTMLElement>('[data-armada-panel]');

			return link && panel ? { root, link, panel } : null;
		})
		.filter((item): item is NavItem => item !== null);

	let closeTimer = 0;
	let pointerX = 0;
	let pointerY = 0;

	document.addEventListener('mousemove', (event) => {
		pointerX = event.clientX;
		pointerY = event.clientY;
	});

	// A mega panel spans the full header width, so a slow diagonal move from a
	// narrow trigger toward a far card crosses sibling items first. Treat the
	// whole header row plus the open panel as one safe zone and keep deferring
	// the close while the pointer is anywhere in it, instead of only the item.
	const isPointerInSafeZone = (item: NavItem): boolean => {
		const headerRect = header.getBoundingClientRect();
		const panelRect = item.panel.getBoundingClientRect();

		const inHeader =
			pointerX >= headerRect.left &&
			pointerX <= headerRect.right &&
			pointerY >= headerRect.top &&
			pointerY <= headerRect.bottom;

		const inPanel =
			pointerX >= panelRect.left &&
			pointerX <= panelRect.right &&
			pointerY >= panelRect.top &&
			pointerY <= panelRect.bottom;

		return inHeader || inPanel;
	};

	const open = (item: NavItem): void => {
		window.clearTimeout(closeTimer);
		items.forEach((other) => other !== item && close(other));

		item.panel.hidden = false;
		item.root.classList.add('is-open');
		item.link.setAttribute('aria-expanded', 'true');
		header.classList.add('is-open');

		window.requestAnimationFrame(() => item.panel.classList.add('is-open'));
	};

	const close = (item: NavItem): void => {
		if (!item.root.classList.contains('is-open')) {
			return;
		}

		item.panel.classList.remove('is-open');
		item.root.classList.remove('is-open');
		item.link.setAttribute('aria-expanded', 'false');

		const hide = (): void => {
			if (!item.panel.classList.contains('is-open')) {
				item.panel.hidden = true;
			}
		};

		item.panel.addEventListener('transitionend', hide, { once: true });
		window.setTimeout(hide, PANEL_FALLBACK);

		if (!items.some((other) => other.root.classList.contains('is-open'))) {
			header.classList.remove('is-open');
		}
	};

	const closeAll = (): void => items.forEach(close);

	items.forEach((item) => {
		item.root.addEventListener('mouseenter', () => {
			if (desktop.matches) {
				open(item);
			}
		});

		item.root.addEventListener('mouseleave', () => {
			if (!desktop.matches) {
				return;
			}

			window.clearTimeout(closeTimer);

			const attemptClose = (): void => {
				if (isPointerInSafeZone(item)) {
					closeTimer = window.setTimeout(attemptClose, CLOSE_DELAY);
				} else {
					close(item);
				}
			};

			closeTimer = window.setTimeout(attemptClose, CLOSE_DELAY);
		});

		item.link.addEventListener('click', (event) => {
			const href = item.link.getAttribute('href') || '#';

			if (!desktop.matches || href === '#') {
				event.preventDefault();
				item.root.classList.contains('is-open') ? close(item) : open(item);
			}
		});

		item.root.addEventListener('focusin', () => {
			if (desktop.matches) {
				open(item);
			}
		});

		item.root.addEventListener('focusout', (event) => {
			const next = event.relatedTarget as Node | null;

			if (desktop.matches && (!next || !item.root.contains(next))) {
				close(item);
			}
		});
	});

	let menuLocked = false;

	const setMenu = (expanded: boolean): void => {
		burger?.setAttribute('aria-expanded', String(expanded));
		nav?.classList.toggle('is-open', expanded);

		document.documentElement.classList.toggle('armada-menu-open', expanded);

		if (expanded && !menuLocked) {
			menuLocked = true;
			lockScroll();
		} else if (!expanded && menuLocked) {
			menuLocked = false;
			unlockScroll();
		}

		if (!expanded) {
			closeAll();
		}
	};

	closeMenu = () => setMenu(false);

	burger?.addEventListener('click', () => {
		setMenu(burger.getAttribute('aria-expanded') !== 'true');
	});

	scrim?.addEventListener('click', () => {
		closeAll();
		setMenu(false);
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			closeAll();
			setMenu(false);
		}
	});

	document.addEventListener('click', (event) => {
		if (!header.contains(event.target as Node)) {
			closeAll();
			setMenu(false);
		}
	});

	desktop.addEventListener('change', () => {
		closeAll();
		setMenu(false);
	});
}
